import {
  Children,
  isValidElement,
  type HTMLProps,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { AnimatePresence } from "motion/react";
import { cx } from "class-variance-authority";
import type { EmblaOptionsType, EmblaPluginType } from "embla-carousel";
import type { ButtonProps } from "../Button/Button";
import { ChevronLeft } from "../Icon/ChevronLeft";
import { ChevronRight } from "../Icon/ChevronRight";
import { Shadow } from "../Shadow/Shadow";
import { Action } from "./CarouselAction";
import * as styles from "./Carousel.module.scss";
import {
  CarouselContext,
  type CarouselControlsType,
  useCarousel,
  useCarouselContext,
} from "./useCarousel";

export type ItemProps = HTMLProps<HTMLDivElement> & {
  className?: string;
};

const Item = ({ className, ...otherProps }: ItemProps) => {
  return (
    <div
      role="group"
      aria-roledescription="slide"
      {...otherProps}
      className={cx(styles.item, className)}
    />
  );
};
Item.displayName = "CarouselItem";

const isItemElement = (node: ReactNode): node is ReactElement<ItemProps> =>
  isValidElement(node) && node.type === Item;

type ViewportProps = {
  children: ReactNode;
  className?: string;
};

const Viewport = ({ children, className }: ViewportProps) => {
  const { viewportRef } = useCarouselContext();

  const childArray = Children.toArray(children);
  const itemElements = childArray.filter(
    isItemElement,
  ) as ReactElement<ItemProps>[];

  // separarte carousel items from other children
  // (Shadows/Control/CustomComponents vs CarouselItem)
  const overlayChildren = childArray.filter((child) => !isItemElement(child));

  return (
    <div ref={viewportRef} className={cx(styles.carouselContainer, className)}>
      <div className={styles.content}>{itemElements}</div>
      {overlayChildren}
    </div>
  );
};
Viewport.displayName = "CarouselViewport";

export type RootProps = {
  children: ReactNode;
  className?: string;
  opts?: EmblaOptionsType;
  plugins?: EmblaPluginType[];
};

const Root = ({ children, className, opts, plugins }: RootProps) => {
  const context = useCarousel(opts, plugins);
  const { scrollPrev, scrollNext } = context;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      scrollPrev();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      scrollNext();
    }
  };

  return (
    <CarouselContext.Provider value={context}>
      <div
        role="region"
        aria-roledescription="carousel"
        onKeyDownCapture={handleKeyDown}
        className={cx(styles.root, className)}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
};
Root.displayName = "CarouselRoot";

type ShadowsProps = {
  className?: string;
};

const Shadows = ({ className }: ShadowsProps) => {
  const { canScrollPrev, canScrollNext } = useCarouselContext();
  return (
    <>
      <AnimatePresence>
        {canScrollPrev && <Shadow className={className} position="left" />}
      </AnimatePresence>
      <AnimatePresence>
        {canScrollNext && <Shadow className={className} position="right" />}
      </AnimatePresence>
    </>
  );
};
Shadows.displayName = "CarouselShadows";

type ControlProps = {
  type: CarouselControlsType;
  className?: string;
};

const Control = ({ type, className }: ControlProps) => {
  const { scrollSnapCount, selectedIndex, scrollTo } = useCarouselContext();
  if (scrollSnapCount <= 1) return null;

  const trackClassName = type === "dots" ? styles.dots : styles.bars;
  const itemClassName = type === "dots" ? styles.dot : styles.bar;

  return (
    <div className={cx(trackClassName, className)}>
      {Array.from({ length: scrollSnapCount }).map((_, i) => (
        <button
          type="button"
          data-testid={`control-${i}`}
          key={i}
          aria-label={`Go to slide ${i + 1}`}
          aria-current={i === selectedIndex ? "true" : undefined}
          className={cx(itemClassName, {
            [styles.active]: i === selectedIndex,
          })}
          onClick={() => scrollTo(i)}
        />
      ))}
    </div>
  );
};

const Previous = ({
  "aria-label": ariaLabel = "Previous slide",
  ...props
}: ButtonProps) => {
  const { scrollPrev, canScrollPrev } = useCarouselContext();
  return (
    <Action
      Icon={ChevronLeft}
      onAction={scrollPrev}
      canAction={canScrollPrev}
      aria-label={ariaLabel}
      {...props}
    />
  );
};
Previous.displayName = "CarouselPrevious";

const Next = ({
  "aria-label": ariaLabel = "Next slide",
  ...props
}: ButtonProps) => {
  const { scrollNext, canScrollNext } = useCarouselContext();
  return (
    <Action
      Icon={ChevronRight}
      onAction={scrollNext}
      canAction={canScrollNext}
      aria-label={ariaLabel}
      {...props}
    />
  );
};
Next.displayName = "CarouselNext";

// Skips the explicit `<Root><Viewport>` nesting for the common case of
// nothing else composed outside Viewport — real consumers (Banners,
// AnnouncementCards) that don't need Previous/Next/Shadows as Root-level
// siblings use this. `className` goes to Root (matches shadcn's own bare
// `Carousel`'s className landing on its outer region div).
const CarouselBase = ({ children, className, ...rootProps }: RootProps) => (
  <Root className={className} {...rootProps}>
    <Viewport>{children}</Viewport>
  </Root>
);
CarouselBase.displayName = "Carousel";

const Carousel = Object.assign(CarouselBase, {
  Root,
  Viewport,
  Item,
  Control,
  Next,
  Previous,
  Shadows,
});

export { Carousel };
